import { Box, ListItemText, MenuItem, styled } from "@mui/material";
import { colors, GhostPlaceholder, Typography } from "@procore/core-react";
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CORE_WINDOW_IDS } from "contexts/windows";
import { useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";

import { useProcoreStamp } from "./useProcoreStamp";

const SCROLLBAR_WIDTH = 6;

const StyledMenuItem = styled(MenuItem)({
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
});

const ScrollViewport = styled(Box)({
    height: "100%",
    overflowY: "scroll",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
});

const ScrollTrack = styled(Box)({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: SCROLLBAR_WIDTH,
    backgroundColor: colors.gray94,
    pointerEvents: "none",
});

const ScrollThumb = styled(Box)({
    position: "absolute",
    right: 0,
    width: SCROLLBAR_WIDTH,
    backgroundColor: colors.gray40,
    borderRadius: 10,
});

function AlwaysVisibleScrollArea({ children }: { children: ReactNode }) {
    const viewportRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState<{ top: number; height: number } | null>(null);

    const updateThumb = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = viewport;
        if (scrollHeight <= clientHeight) {
            setThumb(null);
            return;
        }

        const height = Math.max((clientHeight / scrollHeight) * clientHeight, 20);
        const maxTop = clientHeight - height;
        const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop;
        setThumb({ top, height });
    }, []);

    useLayoutEffect(() => {
        updateThumb();

        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (!viewport) {
            return;
        }

        const observer = new ResizeObserver(updateThumb);
        observer.observe(viewport);
        if (content) {
            observer.observe(content);
        }

        return () => observer.disconnect();
    }, [children, updateThumb]);

    return (
        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
            <ScrollViewport ref={viewportRef} onScroll={updateThumb}>
                <Box ref={contentRef}>{children}</Box>
            </ScrollViewport>
            {thumb ? (
                <ScrollTrack>
                    <ScrollThumb style={{ top: thumb.top, height: thumb.height }} />
                </ScrollTrack>
            ) : null}
        </Box>
    );
}

export function ProcoreMobileContextMenuStamp() {
    const { t } = useTranslation();
    const { togglePanel } = useUnifiedPanelToggle();

    const stampData = useProcoreStamp({
        openProperties: () => togglePanel(CORE_WINDOW_IDS.PROPERTIES),
    });

    if (!stampData) {
        return null;
    }

    const {
        stamp,
        obj,
        isLoading,
        isOnline,
        flyToDisabled,
        multipleObjectsSelected,
        anyObjectsSelected,
        highlightedCount,
        menuItems,
        hide,
        hideSimilar,
        selectSimilar,
        isolateInXRayMode,
        viewProperties,
        zoomToSelection,
        addSectionPlane,
        executeMenuItem,
    } = stampData;

    return (
        <Box
            sx={{
                pointerEvents: "auto",
                width: 250,
                height: 320,
                display: "flex",
                flexDirection: "column",
                borderRadius: "4px",
                bgcolor: "background.paper",
                overflow: "hidden",
            }}
        >
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <Box
                    sx={{
                        flexShrink: 0,
                        padding: "12px",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Typography
                        intent="h3"
                        breakWord
                        style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {multipleObjectsSelected
                            ? `[${highlightedCount}] Objects Selected`
                            : obj?.name || (stamp.data.object ? `Object ${stamp.data.object}` : "")}
                    </Typography>
                </Box>
            )}
            <AlwaysVisibleScrollArea>
                {isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple onClick={hide} disabled={stamp.data.object === undefined}>
                        <ListItemText>
                            <Typography intent="body">{t("hideSelected")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple onClick={hideSimilar} disabled={!isOnline || !obj?.name}>
                        <ListItemText>
                            <Typography intent="body">{t("hideSimilar")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple onClick={viewProperties} disabled={flyToDisabled}>
                        <ListItemText>
                            <Typography intent="body">{t("viewProperties")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {multipleObjectsSelected ? null : isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple onClick={addSectionPlane} disabled={!stamp.data.normal}>
                        <ListItemText>
                            <Typography intent="body">{t("addSectionPlane")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {!anyObjectsSelected ? null : isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem
                        disableRipple
                        onClick={() => {
                            void zoomToSelection();
                        }}
                        disabled={flyToDisabled}
                    >
                        <ListItemText>
                            <Typography intent="body">{t("zoomToSelection")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {multipleObjectsSelected ? null : isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple disabled={!isOnline || !obj?.name} onClick={selectSimilar}>
                        <ListItemText>
                            <Typography intent="body">{t("selectSimilar")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem
                        disableRipple
                        onClick={isolateInXRayMode}
                        disabled={stamp.data.object === undefined}
                    >
                        <ListItemText>
                            <Typography intent="body">{t("isolateInXRayMode")}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                )}
                {menuItems.map((item) =>
                    isLoading ? (
                        <MenuGhostPlaceholder />
                    ) : (
                        <StyledMenuItem disableRipple key={item.id} onClick={() => executeMenuItem(item)}>
                            <ListItemText>
                                <Typography intent="body">{item.label}</Typography>
                            </ListItemText>
                        </StyledMenuItem>
                    ),
                )}
            </AlwaysVisibleScrollArea>
        </Box>
    );
}

function MenuGhostPlaceholder() {
    return (
        <Box sx={{ padding: "12px" }}>
            <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
        </Box>
    );
}
